import { Command } from 'commander';
import { Jai1Error } from './errors/index.js';
import { ErrorLogService } from './services/error-log.service.js';
import { basename } from 'path';
import packageJson from '../package.json' with { type: 'json' };
import { checkForClientUpdate } from './services/version-check.service.js';
import { getCliName } from './utils/cli-name.js';

// ============================================
// SETUP & AUTH COMMANDS
// ============================================
import { createAuthCommand } from './commands/framework/auth.js';
import { createStatusCommand } from './commands/status.js';

// ============================================
// COMPONENT MANAGEMENT COMMANDS
// ============================================
import { createApplyCommand } from './commands/apply.js';
import { createUpdateCommand } from './commands/update.js';
import { createCheckCommand } from './commands/framework/check.js';

// ============================================
// IDE COMMANDS (grouped)
// ============================================
import { createIdeCommand } from './commands/ide/index.js';

// ============================================
// GUIDE & DOCTOR COMMANDS
// ============================================
import { createGuideCommand } from './commands/guide.js';
import { createQuickstartCommand } from './commands/quickstart.js';
import { createDoctorCommand } from './commands/doctor.js';

// ============================================
// LLM PROXY COMMAND
// ============================================
import { createChatCommand } from './commands/chat.js';
import { createOpenAiKeysCommand } from './commands/openai-keys.js';
import { createStatsCommand } from './commands/stats.js';
import { createTranslateCommand } from './commands/translate.js';
import { createImageCommand } from './commands/image/index.js';
import { createFeedbackCommand } from './commands/feedback.js';
import { createClientInfoCommand } from './commands/client-info.js';
import { createErrorsCommand } from './commands/errors/index.js';

// ============================================
// FILE UPLOAD
// ============================================
import { createFileCommand } from './commands/file/index.js';

// ============================================
// DEVELOPER UTILITIES
// ============================================
import { createUtilsCommand } from './commands/utils/index.js';
import { createDepsCommand } from './commands/deps/index.js';
import { createDevCommand } from './commands/dev/index.js';
import { createConnectCommand } from './commands/connect/index.js';
import { createVaultCommand } from './commands/vault/index.js';

// ============================================
// GIT HOOKS
// ============================================
import { createHooksCommand } from './commands/hooks/index.js';

// ============================================
// PROJECT SETTINGS
// ============================================
import { createSettingsCommand } from './commands/settings/index.js';
import { createProjectCommand } from './commands/project/index.js';

// ============================================
// TASK MANAGEMENT
// ============================================
import { createTasksCommand } from './commands/tasks/index.js';

// ============================================
// STARTER KITS
// ============================================
import { createKitCommand } from './commands/kit/index.js';
// ============================================
import { createRulesCommand } from './commands/rules/index.js';
// ============================================
import { createSkillsCommand } from './commands/skills/index.js';



// ============================================
// MAINTENANCE COMMANDS
// ============================================
import { createUpgradeCommand } from './commands/upgrade.js';
import { createCleanCommand } from './commands/clean.js';

// ============================================
// EXTERNAL INTEGRATIONS
// ============================================
import { createRedmineCheckCommand } from './commands/redmine/check.js';
import { createSyncIssueCommand } from './commands/redmine/sync-issue.js';
import { createSyncProjectCommand } from './commands/redmine/sync-project.js';

// ============================================
// LEGACY ALIASES (for backward compatibility)
// ============================================
import { createInfoCommand } from './commands/framework/info.js';
import { createSelfUpdateCommand } from './commands/self-update.js';
import { createClearBackupsCommand } from './commands/clear-backups.js';
import { createVSCodeCommand } from './commands/vscode/index.js';
import { createContextCommand } from './commands/context.js';
import { createMigrateIdeCommand } from './commands/migrate-ide.js';

// ============================================
// CUSTOM HELP
// ============================================
import { showCustomHelp, showUnknownCommand } from './utils/help-formatter.js';

const program = new Command();

// Manual version check to support update checking
if (process.argv.includes('-v') || process.argv.includes('--version')) {
  console.log(packageJson.version);
  // Check for updates
  if (!process.argv.includes('--skip-update-check')) {
    await checkForClientUpdate();
  }
  process.exit(0);
}

program
  .name(getCliName())
  .description('Jai1 Framework Management CLI')
  .configureHelp({
    formatHelp: () => {
      showCustomHelp(packageJson.version);
      return '';
    },
  });
// .version() removed to custom handle update check (handled above)

// Global options (hidden from help)
program
  // .option('-v, --version', 'output the version number') // Handled manually
  .option('--verbose', 'Enable verbose logging', false)
  .option('--json', 'Output results as JSON', false)
  .option('--skip-update-check', 'Skip checking for client updates', false);

// ============================================
// 🔧 SETUP & INFO
// ============================================
program.addCommand(createAuthCommand());
program.addCommand(createStatusCommand());

// ============================================
// 📦 COMPONENT MANAGEMENT
// ============================================
program.addCommand(createApplyCommand());
program.addCommand(createUpdateCommand());
program.addCommand(createCheckCommand());

// ============================================
// 🖥️ IDE INTEGRATION (grouped)
// ============================================
program.addCommand(createIdeCommand());

// ============================================
// 📚 GUIDE & DOCTOR
// ============================================
program.addCommand(createGuideCommand());
program.addCommand(createQuickstartCommand());
program.addCommand(createDoctorCommand());

// ============================================
// 🤖 LLM PROXY
// ============================================
program.addCommand(createChatCommand());
program.addCommand(createOpenAiKeysCommand());
program.addCommand(createStatsCommand());
program.addCommand(createTranslateCommand());
program.addCommand(createImageCommand());
program.addCommand(createFeedbackCommand());
program.addCommand(createClientInfoCommand());
program.addCommand(createErrorsCommand());

// ============================================
// 📁 FILE UPLOAD
// ============================================
program.addCommand(createFileCommand());

// ============================================
// 🛠️ DEVELOPER UTILITIES
// ============================================
program.addCommand(createUtilsCommand());
program.addCommand(createDepsCommand());
program.addCommand(createDevCommand());
program.addCommand(createConnectCommand());
program.addCommand(createVaultCommand());

// ============================================
// ⚙️ PROJECT SETTINGS
// ============================================
program.addCommand(createSettingsCommand());
program.addCommand(createProjectCommand());

// ============================================
// 📋 TASK MANAGEMENT
// ============================================
program.addCommand(createTasksCommand());

// ============================================
// 📦 STARTER KITS
// ============================================
program.addCommand(createKitCommand());
// ============================================
// 📋 RULE PRESETS
// ============================================
program.addCommand(createRulesCommand());
// ============================================
// 🛠 AGENT SKILLS
// ============================================
program.addCommand(createSkillsCommand());
// ============================================
// 🔗 GIT HOOKS
// ============================================
program.addCommand(createHooksCommand());


// ============================================
// 🔧 MAINTENANCE
// ============================================
program.addCommand(createUpgradeCommand());
program.addCommand(createCleanCommand());

// ============================================
// 🔗 EXTERNAL INTEGRATIONS
// ============================================
const redmineCommand = new Command('redmine')
  .description('Redmine context sync commands');

redmineCommand.addCommand(createRedmineCheckCommand());

const syncCommand = new Command('sync')
  .description('Sync Redmine issues to markdown files');

syncCommand.addCommand(createSyncIssueCommand());
syncCommand.addCommand(createSyncProjectCommand());

redmineCommand.addCommand(syncCommand);

program.addCommand(redmineCommand);

// ============================================
// 🔄 LEGACY ALIASES (backward compatibility)
// Hidden from help, but still work
// ============================================


// Legacy: info -> status
program.addCommand(createInfoCommand(), { hidden: true });

// Legacy: self-update -> upgrade
program.addCommand(createSelfUpdateCommand(), { hidden: true });

// Legacy: clear-backups -> clean
program.addCommand(createClearBackupsCommand(), { hidden: true });

// Legacy: vscode -> ide setup
program.addCommand(createVSCodeCommand(), { hidden: true });

// Legacy: context -> ide context
program.addCommand(createContextCommand(), { hidden: true });

// Legacy: migrate-ide -> ide sync
program.addCommand(createMigrateIdeCommand(), { hidden: true });


// Handle unknown commands
program.on('command:*', (operands) => {
  showUnknownCommand(operands[0]);
  process.exit(1);
});

// Error handling
process.on('unhandledRejection', (error) => {
  // Best-effort local error log (never blocks exit)
  void new ErrorLogService().logError(error, {
    command: process.argv.slice(2).join(' '),
    cwdBase: basename(process.cwd()),
  });

  if (error instanceof Jai1Error) {
    console.error('❌', error.message);
    process.exit(error.exitCode);
  } else if (error instanceof Error) {
    console.error('❌ Unexpected error:', error.message);
    if (program.opts().verbose) {
      console.error(error.stack);
    }
    process.exit(1);
  }
});

// Parse and execute
program.parseAsync(process.argv).then(async () => {
  const opts = program.opts();

  // Check for client updates after command execution (unless skipped)
  if (!opts.skipUpdateCheck) {
    await checkForClientUpdate();
  }
}).catch((error) => {
  // Best-effort local error log (never blocks exit)
  void new ErrorLogService().logError(error, {
    command: process.argv.slice(2).join(' '),
    cwdBase: basename(process.cwd()),
  });

  console.error('❌', error.message);
  process.exit(1);
});
